import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../components/SubscriptionGuard';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowUpCircle,
  Check,
  Users,
  BarChart3,
  Palmtree,
  ClipboardCheck,
  CalendarDays,
  Bell,
  Bot,
  CreditCard,
  Timer,
  Clock,
  FolderKanban,
  ShieldCheck,
  ArrowRight,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Upgrade = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { refreshSubscription } = useSubscription() || {};
  const [upgrading, setUpgrading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [trialUsed, setTrialUsed] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkTrialStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTrialUsed(!!data.trial_started_at);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { checkTrialStatus(); }, [checkTrialStatus]);

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      if (trialUsed) {
        // Redirect to billing page where payment options exist
        setConfirmOpen(false);
        navigate('/billing');
        return;
      } else {
        // Start free trial
        const res = await fetch(`${API_URL}/api/payments/upgrade-to-business`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Upgrade failed');
        toast.success('Upgraded to Business Plan! Your 14-day trial has started.');
      }
      setConfirmOpen(false);
      if (refreshSubscription) refreshSubscription();
      setTimeout(() => navigate('/admin/dashboard'), 1500);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpgrading(false);
    }
  };

  const freePlanFeatures = [
    { icon: Timer, label: 'Time Tracking' },
    { icon: FolderKanban, label: 'Projects & Clients' },
    { icon: Clock, label: 'Timesheet & Export' },
  ];

  const businessPlanFeatures = [
    { icon: Users, label: 'Unlimited Team Members' },
    { icon: ClipboardCheck, label: 'Time Approvals' },
    { icon: Palmtree, label: 'Time Off Management' },
    { icon: CalendarDays, label: 'Holiday Calendar' },
    { icon: BarChart3, label: 'Advanced Reports' },
    { icon: Bell, label: 'Notifications & Alerts' },
    { icon: Bot, label: 'AI Assistant' },
    { icon: CreditCard, label: 'Billing & Invoicing' },
  ];

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="upgrade-page">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
            Upgrade to Business Plan
          </h1>
          <p className="text-slate-500 mt-2 text-base">
            Unlock team collaboration, approvals, leave management and more
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Individual Plan */}
          <div className="premium-card p-6 border-2 border-slate-200 dark:border-slate-700 relative" data-testid="individual-plan-card">
            <div className="absolute -top-3 left-4">
              <span className="bg-slate-600 dark:bg-slate-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                Current Plan
              </span>
            </div>
            <div className="mt-2 mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Individual Plan</h2>
              <div className="mt-2">
                <span className="text-3xl font-bold text-slate-900 dark:text-white">Free</span>
                <span className="text-slate-500 ml-1">forever</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">For freelancers and solo professionals</p>
            </div>
            <div className="space-y-3 mt-6">
              {freePlanFeatures.map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-3.5 h-3.5 text-slate-500" />
                  </div>
                  <span className="text-slate-600 dark:text-slate-400">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Business Plan */}
          <div className="premium-card p-6 border-2 border-blue-500 dark:border-blue-400 relative" data-testid="business-plan-card">
            <div className="absolute -top-3 left-4">
              <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                Recommended
              </span>
            </div>
            <div className="mt-2 mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Business Plan</h2>
              <div className="mt-2">
                <span className="text-3xl font-bold text-slate-900 dark:text-white">&#8377;199</span>
                <span className="text-slate-500 ml-1">/user/month</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {trialUsed ? 'Subscribe to get started' : '14-day free trial included'}
              </p>
            </div>
            <div className="space-y-3 mt-6">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Everything in Individual, plus:
              </p>
              {businessPlanFeatures.map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <div className="w-6 h-6 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="text-slate-700 dark:text-slate-300">{f.label}</span>
                </div>
              ))}
            </div>
            <Button
              onClick={() => setConfirmOpen(true)}
              className="w-full mt-6 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-base"
              data-testid="upgrade-to-business-btn"
            >
              {trialUsed ? (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Upgrade to Business Plan
                </>
              ) : (
                <>
                  <ArrowUpCircle className="w-5 h-5 mr-2" />
                  Start 14-Day Free Trial
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Upgrade Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="upgrade-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ArrowUpCircle className="w-6 h-6 text-blue-600" />
              {trialUsed ? 'Subscribe to Business Plan' : 'Upgrade to Business Plan'}
            </DialogTitle>
            <DialogDescription>
              {trialUsed
                ? 'You will be redirected to complete payment for the Business plan.'
                : "You're about to switch from the Individual plan to the Business plan."}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-500/20">
              <ShieldCheck className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                  All your data will be migrated
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  Your projects, clients, time entries, timesheets, and all existing data will be fully carried over to the Business plan. Nothing will be lost.
                </p>
              </div>
            </div>

            <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
              {trialUsed ? (
                <>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-emerald-500" />
                    <span>All Business features unlocked after payment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-emerald-500" />
                    <span>Invite team members and enable approvals</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-emerald-500" />
                    <span>&#8377;199/user/month — cancel anytime</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-emerald-500" />
                    <span>14-day free trial starts immediately</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-emerald-500" />
                    <span>All Business features unlocked instantly</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-emerald-500" />
                    <span>Invite team members and enable approvals</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-emerald-500" />
                    <span>No charge until trial ends</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="upgrade-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleUpgrade}
              disabled={upgrading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="upgrade-confirm-btn"
            >
              {upgrading ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
              ) : trialUsed ? (
                'Proceed to Payment'
              ) : (
                'Confirm Upgrade'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Upgrade;
